import { FC, useState } from 'react';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import { useLabResults } from './useLabResults';
import { LabResultsFilter } from './LabResultsFilter';
import { ResultsTable } from './ResultsTable';
import { LabTrends } from './LabTrends';
import { PageWrapper } from './styles';
import { Colors, FontSize, Spacing } from '../../../assets/GlobalStyle/Variables';

type InnerTab = 'results' | 'trends';

export const LabResultsPage: FC = () => {
  const [activeTab, setActiveTab] = useState<InnerTab>('results');

  const {
    samplings,
    selectedSampling,
    filterType,
    dateRange,
    loading,
    parameterDefinitions,
    laboratoryAreas,
    setFilterType,
    applyCustomDateRange,
    handleSamplingSelect,
    copyToClipboard
  } = useLabResults();

  return (
    <PageWrapper>
      <LabResultsFilter
        filterType={filterType}
        dateRange={dateRange}
        onFilterTypeChange={setFilterType}
        onDateRangeChange={applyCustomDateRange}
      />

      <InnerTabList>
        <InnerTabButton $active={activeTab === 'results'} onClick={() => setActiveTab('results')}>
          Výsledky komplementu
        </InnerTabButton>
        <InnerTabButton $active={activeTab === 'trends'} onClick={() => setActiveTab('trends')}>
          Laboratorní trendy
        </InnerTabButton>
      </InnerTabList>

      {loading ? (
        <SkeletonLayout>
          <div>
            <Skeleton height={42} style={{ marginBottom: '1px' }} />
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} height={36} style={{ marginBottom: '1px' }} />
            ))}
          </div>
          <div>
            <Skeleton height={42} style={{ marginBottom: '12px' }} />
            <Skeleton height={20} width={220} style={{ marginBottom: '6px' }} />
            <Skeleton height={20} width={180} style={{ marginBottom: '16px' }} />
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} height={32} style={{ marginBottom: '2px' }} />
            ))}
          </div>
        </SkeletonLayout>
      ) : (
        <>
          {activeTab === 'results' && (
            <ResultsTable
              samplings={samplings}
              selectedSampling={selectedSampling}
              onSelect={handleSamplingSelect}
              onCopy={copyToClipboard}
            />
          )}
          {activeTab === 'trends' && (
            <LabTrends
              samplings={samplings}
              parameterDefinitions={parameterDefinitions}
              laboratoryAreas={laboratoryAreas}
              onCopy={(results) => copyToClipboard(results as any)}
            />
          )}
        </>
      )}
    </PageWrapper>
  );
};

const SkeletonLayout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: ${Spacing(4)};
`;

const InnerTabList = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid ${Colors.lightGrey};
`;

const InnerTabButton = styled.button<{ $active: boolean }>`
  all: initial;
  font-family: 'Arial Nova', sans-serif;
  cursor: pointer;
  padding: 0.6rem 1.5rem;
  font-size: ${FontSize.normal};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active }) => ($active ? Colors.secondary : Colors.mainText)};
  border-bottom: 3px solid ${({ $active }) => ($active ? Colors.secondary : 'transparent')};
  margin-bottom: -2px;
  transition:
    color 0.2s,
    border-color 0.2s;

  &:hover,
  &:focus {
    color: ${Colors.secondary};
    outline: none;
  }
`;
